import { Alert, Button, Col, Collapse, Form, ProgressBar, Row, Tab, Tabs, Pagination } from 'react-bootstrap';
import { annualReportingSignal } from 'components/global/Reporting/Reporting.signals';
import { fetchAndSetAnnualReport, handleDownloadAnnualReport, fetchProviderDataPaginated, fetchCapTransactionsPaginated } from '../Reporting.helpers';
import ReportingTable from './ReportingTable';
import TestModeBanner from '../../utils/TestModeBanner';
import {
  LoanAndInvestment,
  ProviderData,
  TransactionEquity,
  TransactionTermsCredit,
} from './ReportingChartsAndTables';
import Loader from '../../Loader';
import { useEffect, useRef, useState } from 'react';

const AnnualReporting = () => {
  const { reportData, fetchingTableData, yearOptions, selectedYear, downloadingReport, downloadProgress, downloadStep } = annualReportingSignal.value;
  const [showProgressAlert, setShowProgressAlert] = useState(false);
  const [collapseIn, setCollapseIn] = useState(false);
  const expandScheduledRef = useRef(false);

  useEffect(() => {
    if (downloadingReport) {
      setShowProgressAlert(true);
      setCollapseIn(false);
      expandScheduledRef.current = true;
      const id = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (expandScheduledRef.current) setCollapseIn(true);
        });
      });
      return () => {
        expandScheduledRef.current = false;
        cancelAnimationFrame(id);
      };
    }
  }, [downloadingReport]);

  useEffect(() => {
    if (!downloadingReport && showProgressAlert) setCollapseIn(false);
  }, [downloadingReport, showProgressAlert]);

  if (fetchingTableData) {
    return <Loader message="Loading report..." />;
  }

  return (
    <>
      {showProgressAlert && (
        <Collapse in={collapseIn} onExited={() => setShowProgressAlert(false)} timeout={600}>
          <div>
            <Alert variant="primary" className="mb-3 mb-lg-16">
              <div className="mb-2">
                Generating Annual Report file. This may take several minutes. You can continue viewing this page.
                {downloadStep && <span className="d-block mt-1 small">{downloadStep}</span>}
              </div>
              <ProgressBar
                variant="primary"
                animated
                striped
                now={downloadProgress}
                className="mb-0"
                style={{ height: 28, lineHeight: '28px', paddingTop: 0, paddingBottom: 0 }}
                label={`${downloadProgress}%`}
              />
            </Alert>
          </div>
        </Collapse>
      )}
      <Row className="align-items-center mb-16">
        <Col className="d-flex justify-content-between align-items-center">
          <div>
            {yearOptions && (
              <Form.Group controlId="annualReportYear" className="mb-0 d-flex align-items-center">
                <Form.Label className="mb-0 mr-8 font-weight-bold text-nowrap">
                  Report year
                </Form.Label>
                <Form.Select
                  className="form-control d-inline-block w-auto"
                  value={selectedYear ?? ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    const year = value === '' ? null : Number(value);
                    fetchAndSetAnnualReport(year);
                  }}
                >
                  <option value="">All</option>
                  {yearOptions.years?.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}
          </div>
          <div>
            {reportData && (
              <Button
                variant="primary"
                className="rounded"
                onClick={handleDownloadAnnualReport}
                disabled={selectedYear == null || downloadingReport}
                title={
                  selectedYear == null
                    ? 'Select a specific report year to download the full report.'
                    : downloadingReport
                      ? 'Download in progress.'
                      : undefined
                }
              >
                Download File
              </Button>
            )}
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          {reportData ? (
            <AnnualReportingTabs />
          ) : (
            <></>
          )}
        </Col>
      </Row>
    </>
  );
};

const CAP_TRANSACTIONS_TAB_NAME = 'CAP Transactions';

const AnnualReportingTabs = () => {
  const { selectedTab, reportData, selectedYear, providerData, capTransactions } = annualReportingSignal.value;

  const handleTabSelect = (tab) => {
    annualReportingSignal.update({ selectedTab: tab });
    if (tab === CAP_TRANSACTIONS_TAB_NAME) {
      fetchCapTransactionsPaginated(selectedYear, 1);
    } else if (tab.toLowerCase().includes('provider')) {
      fetchProviderDataPaginated(1, providerData.pagination.pageSize);
    }
  };

  const handlePageChange = (page) => {
    fetchCapTransactionsPaginated(selectedYear, page, capTransactions.pagination.pageSize);
  };

  const handleProviderPageChange = (page) => {
    fetchProviderDataPaginated(page, providerData.pagination.pageSize);
  };

  useEffect(() => {
    if (selectedTab === CAP_TRANSACTIONS_TAB_NAME && capTransactions.data.length === 0 && !capTransactions.loading) {
      fetchCapTransactionsPaginated(selectedYear, 1);
    }
    if (selectedTab && selectedTab.toLowerCase().includes('provider') && providerData.data.length === 0 && !providerData.loading) {
      fetchProviderDataPaginated(1, providerData.pagination.pageSize);
    }
  }, [selectedTab, selectedYear]);

  const renderPagination = () => {
    const { pagination } = capTransactions;
    if (pagination.totalPages <= 1) return null;

    const items = [];
    const maxVisible = 5;
    let startPage = Math.max(1, pagination.page - Math.floor(maxVisible / 2));
    let endPage = Math.min(pagination.totalPages, startPage + maxVisible - 1);

    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    if (startPage > 1) {
      items.push(
        <Pagination.First key="first" onClick={() => handlePageChange(1)} />
      );
      items.push(
        <Pagination.Prev key="prev" onClick={() => handlePageChange(pagination.page - 1)} disabled={pagination.page === 1} />
      );
    }

    for (let i = startPage; i <= endPage; i++) {
      const isActive = i === pagination.page;
      items.push(
        <Pagination.Item
          key={i}
          className={isActive ? 'active' : ''}
          onClick={() => handlePageChange(i)}
          aria-label={isActive ? `Page ${i}` : `Go to page ${i}`}
          aria-current={isActive ? 'page' : undefined}
        >
          {i}
        </Pagination.Item>
      );
    }

    if (endPage < pagination.totalPages) {
      items.push(
        <Pagination.Next key="next" onClick={() => handlePageChange(pagination.page + 1)} disabled={pagination.page === pagination.totalPages} />
      );
      items.push(
        <Pagination.Last key="last" onClick={() => handlePageChange(pagination.totalPages)} />
      );
    }

    return (
      <>
        <style>{`
          .pagination .page-item.active .page-link .sr-only,
          .pagination .page-item.active .page-link span.sr-only,
          .pagination .page-item.active .page-link::after {
            display: none !important;
            content: none !important;
          }
        `}</style>
        <div className="d-flex justify-content-between align-items-center mt-16 mb-16">
          <div>
            Showing {((pagination.page - 1) * pagination.pageSize) + 1} to {Math.min(pagination.page * pagination.pageSize, pagination.total)} of {pagination.total} results
          </div>
          <Pagination>{items}</Pagination>
        </div>
      </>
    );
  };

  const renderProviderPagination = () => {
    const { pagination } = providerData;
    if (!pagination || pagination.totalPages <= 1) return null;

    const items = [];
    const maxVisible = 5;
    let startPage = Math.max(1, pagination.page - Math.floor(maxVisible / 2));
    let endPage = Math.min(pagination.totalPages, startPage + maxVisible - 1);

    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    if (startPage > 1) {
      items.push(
        <Pagination.First key="first-provider" onClick={() => handleProviderPageChange(1)} />
      );
      items.push(
        <Pagination.Prev
          key="prev-provider"
          onClick={() => handleProviderPageChange(pagination.page - 1)}
          disabled={pagination.page === 1}
        />
      );
    }

    for (let i = startPage; i <= endPage; i++) {
      const isActive = i === pagination.page;
      items.push(
        <Pagination.Item
          key={i}
          className={isActive ? 'active' : ''}
          onClick={() => handleProviderPageChange(i)}
          aria-label={isActive ? `Page ${i}` : `Go to page ${i}`}
          aria-current={isActive ? 'page' : undefined}
        >
          {i}
        </Pagination.Item>
      );
    }

    if (endPage < pagination.totalPages) {
      items.push(
        <Pagination.Next
          key="next-provider"
          onClick={() => handleProviderPageChange(pagination.page + 1)}
          disabled={pagination.page === pagination.totalPages}
        />
      );
      items.push(
        <Pagination.Last
          key="last-provider"
          onClick={() => handleProviderPageChange(pagination.totalPages)}
        />
      );
    }

    return (
      <div className="d-flex justify-content-between align-items-center mt-16 mb-16">
        <div>
          Showing {((pagination.page - 1) * pagination.pageSize) + 1} to{' '}
          {Math.min(pagination.page * pagination.pageSize, pagination.total)} of{' '}
          {pagination.total} providers
        </div>
        <Pagination>{items}</Pagination>
      </div>
    );
  };

  const tabs = [];
  reportData.forEach((bundle, index) => {
    const isProvider = bundle.name.toLowerCase().includes('provider');

    tabs.push(
      <Tab title={bundle.name} eventKey={bundle.name} key={`tab-${index}`}>
        <div className="border-top">
          <h4 className="my-24 text-center">{bundle.name}</h4>
          {/* Provider tab uses paginated providerData */}
          {isProvider ? (
            <>
              {providerData.loading ? (
                <Loader message="Loading Provider Data..." />
              ) : (
                <>
                  {renderProviderPagination()}
                  <ReportingTable
                    data={providerData.data}
                    headers={providerData.headers}
                  />
                  {renderProviderPagination()}
                </>
              )}
              <ProviderData bundle={bundle} />
            </>
          ) : (
            <>
              <ReportingTable data={bundle.data} headers={bundle.headers} />
              {bundle.name.toLowerCase().includes('provider') && (
                <ProviderData bundle={bundle} />
              )}
            </>
          )}
          {bundle.name.toLowerCase().includes('investment') &&
            bundle.name.toLowerCase().includes('info') && (
              <LoanAndInvestment bundle={bundle} />
            )}
          {bundle.name.toLowerCase().includes('credit') && (
            <TransactionTermsCredit bundle={bundle} />
          )}
          {bundle.name.toLowerCase().includes('equity') && (
            <TransactionEquity bundle={bundle} />
          )}
        </div>
      </Tab>
    );

    if (isProvider) {
      tabs.push(
        <Tab title={CAP_TRANSACTIONS_TAB_NAME} eventKey={CAP_TRANSACTIONS_TAB_NAME} key="cap-transactions">
          <div className="border-top">
            <h4 className="my-24 text-center">{CAP_TRANSACTIONS_TAB_NAME}</h4>
            {capTransactions.loading ? (
              <Loader message="Loading CAP Transactions..." />
            ) : (
              <>
                {renderPagination()}
                <ReportingTable data={capTransactions.data} headers={capTransactions.headers} />
                {renderPagination()}
              </>
            )}
          </div>
        </Tab>
      );
    }
  });

  return (
    <>
      <TestModeBanner />
      <Tabs
        className="mt-16"
        activeKey={selectedTab}
        onSelect={handleTabSelect}
      >
        {tabs}
      </Tabs>
    </>
  );
};

export default AnnualReporting;
